import { makeStyles } from '@rentacenter/racstrap';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { PoFormField } from '../../constants/constants';
import { getStoreDetailByNumber } from '../../api/store';
import { Store } from '../../domain/Store';
import { PurchaseOrderFormValues, QrScannerOutput } from '../../types/types';
import { LocalSourceInventoryDetails } from './LocalSourceInventoryDetails';
import { PricingWrapper } from './PricingWrapper';
import { LocalSourceAllocations } from './LocalSourceAllocations';
import { LocalSourceItemDetails } from './LocalSourceItemDetails';
import {
  usePreFillFromDashboard,
  useLocalSourcingPrefillCount,
  useLocalSourcingPrefillPurchaseOrders,
} from './usePreFillFromDashboard';

const MAX_PRODUCTS = 4;

const buildExistingDetailPO = (
  purchaseOrder: PurchaseOrderFormValues | null,
  productIndex: number
): any | null => {
  const detail = purchaseOrder?.purchaseOrderDetails?.[productIndex];
  if (!detail) return null;
  return {
    department: detail.department || '',
    subDepartment: detail.subDepartment || '',
    bracket: detail.bracket || '',
    itemType: detail.itemType || '',
    itemSubType: detail.itemSubType || '',
    startClassificationsSelect: 'department',
    supplier: detail.supplierName || '',
    brand: detail.brand || '',
    model: detail.modelNumber || '',
    description: detail.itemDescription || '',
    costPerItem: detail.itemCost || '',
    itemCondition: detail.condition || '',
    supplierOrderNo: detail.supplierOrderNumber || '',
  };
};

const buildDuplicateDetailPO = (
  formProducts: any,
  productIndex: number
): any | null => {
  const product = formProducts?.[productIndex];
  if (!product) return null;
  return {
    department: product[PoFormField.Department] || '',
    subDepartment: product[PoFormField.SubDepartment] || '',
    bracket: product[PoFormField.Bracket] || '',
    itemType: product[PoFormField.ItemType] || '',
    itemSubType: product[PoFormField.ItemSubType] || '',
    startClassificationsSelect: PoFormField.Department,
    supplier: product[PoFormField.Supplier] || '',
    brand: product[PoFormField.Brand] || '',
    model: product[PoFormField.Model] || '',
    description: product[PoFormField.Description] || '',
    costPerItem: product[PoFormField.CostPerItem] || '',
    itemCondition: product[PoFormField.Condition] || '',
    supplierOrderNo: product[PoFormField.SupplierOrderNo] || '',
  };
};

const CLASSIFICATION_FIELDS = [
  PoFormField.Department,
  PoFormField.SubDepartment,
  PoFormField.Bracket,
  PoFormField.ItemType,
  PoFormField.ItemSubType,
  PoFormField.StartClassificationsSelect,
];

const reapplyDuplicateClassifications = (
  indexes: number[],
  formProducts: any,
  setValue: (name: string, value: any) => void
) => {
  setTimeout(() => {
    indexes.forEach((idx) => {
      if (idx > 0 && formProducts[idx]) {
        const product = formProducts[idx];
        const prefix = `products[${idx}]`;
        CLASSIFICATION_FIELDS.forEach((field) => {
          if (product[field]) {
            setValue(`${prefix}.${field}`, product[field]);
          }
        });
      }
    });
  }, 0);
};

const getFilledProductIndexes = (formProducts: any): number[] => {
  return Object.keys(formProducts)
    .map(Number)
    .filter(
      (idx) =>
        !isNaN(idx) &&
        formProducts[idx] &&
        formProducts[idx][PoFormField.Department]
    );
};

const REQUIRED_LOCAL_SOURCE_PRODUCT_FIELDS = [
  PoFormField.Supplier,
  PoFormField.Brand,
  PoFormField.Model,
  PoFormField.Condition,
  PoFormField.Description,
  PoFormField.Department,
  PoFormField.SubDepartment,
  PoFormField.Bracket,
  PoFormField.ItemType,
  PoFormField.ItemSubType,
  PoFormField.CostPerItem,
];
const LOCAL_SOURCE_PRODUCT_FIELDS = [
  ...REQUIRED_LOCAL_SOURCE_PRODUCT_FIELDS,
  PoFormField.SupplierOrderNo,
  PoFormField.StartClassificationsSelect,
];
const PRIMARY_ALLOCATION_STATE_FIELD = 'allocations[0].state';
const PRIMARY_ALLOCATION_STORE_FIELD = 'allocations[0].storeNum';
const REQUIRED_LOCAL_SOURCE_FORM_FIELDS = [
  PoFormField.PurchaseOrderSubType,
  PoFormField.ReferenceNumber,
  PRIMARY_ALLOCATION_STORE_FIELD,
  PRIMARY_ALLOCATION_STATE_FIELD,
];
const hasRequiredValue = (value: any): boolean =>
  value !== undefined &&
  value !== null &&
  !(typeof value === 'string' && value.trim() === '');
const getInputValue = (fieldName: string): string =>
  (document.getElementsByName(fieldName)[0] as HTMLInputElement | undefined)
    ?.value || '';
export interface LocalSourceRampDetailsProps {
  readonly purchaseOrder: PurchaseOrderFormValues | null;
  readonly stores: Store[];
  readonly disabled: boolean;
  readonly qrScannerOutput: QrScannerOutput | undefined;
  readonly isCoupaOrPcrdSelected?: boolean;
  readonly isSearchLoading?: boolean;
  readonly onSearch?: (text: string) => void;
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    padding: theme.typography.pxToRem(8),
    marginBottom: theme.typography.pxToRem(124),
    backgroundColor: '#f1f5f8',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'minmax(37.5rem, 1fr) minmax(37.5rem, 1fr)',
    gap: theme.typography.pxToRem(16),
    alignItems: 'start',
    '& > div': {
      minWidth: 0,
      maxWidth: 'none',
    },
    ['@media (max-width:1240px)']: {
      gridTemplateColumns: '1fr',
    },
  },
  productDetails: {
    marginBottom: theme.typography.pxToRem(16),
  },
}));

/**
 * Feature-gated Manual PO layout. Its separate composition prevents Local
 * Source Ramp changes from affecting the legacy Manual PO experience.
 */
export const LocalSourceRampDetails = ({
  purchaseOrder,
  stores,
  disabled,
  qrScannerOutput,
  isCoupaOrPcrdSelected,
  isSearchLoading,
  onSearch,
}: LocalSourceRampDetailsProps) => {
  const classes = useStyles();
  const initialProductIndexes = useLocalSourcingPrefillCount();
  // history.replace in usePreFillFromDashboard clears location.state on next render, losing prefill data
  const [prefillPurchaseOrders] = useState(
    useLocalSourcingPrefillPurchaseOrders()
  );
  const existingDetailCount = purchaseOrder?.purchaseOrderDetails?.length || 0;
  const [productIndexes, setProductIndexes] = useState(
    existingDetailCount > 1
      ? Array.from(
          {
            length: Math.min(existingDetailCount, MAX_PRODUCTS),
          },
          (_, i) => i
        )
      : initialProductIndexes
  );
  const [pricingRequestTrigger, setPricingRequestTrigger] = useState(0);
  const { clearErrors, getValues, setValue, trigger, unregister } =
    useFormContext();
  const allocations = useWatch({ name: 'allocations' }) as any[];
  const primaryState = allocations?.[0]?.state;
  const primaryStoreNum = allocations?.[0]?.storeNum;

  usePreFillFromDashboard();

  // Detect duplicated products from useDuplicatePo reset
  useEffect(() => {
    const formProducts = getValues('products');
    if (!formProducts || existingDetailCount > 1) return;
    const filledIndexes = getFilledProductIndexes(formProducts);
    if (filledIndexes.length > 0) {
      const allIndexes = [0, ...filledIndexes.filter((i) => i > 0)];
      const unique = Array.from(new Set(allIndexes)).sort((a, b) => a - b);
      if (unique.length > productIndexes.length) {
        setProductIndexes(unique.slice(0, MAX_PRODUCTS));
        reapplyDuplicateClassifications(unique, formProducts, setValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('localSourceProductIndexes', productIndexes);
    if (primaryState && primaryStoreNum) {
      productIndexes.forEach((idx) => {
        if (idx > 0) {
          setValue(`allocations[${idx}].state`, primaryState, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue(`allocations[${idx}].storeNum`, primaryStoreNum, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    }
  }, [primaryState, primaryStoreNum, productIndexes, setValue]);

  const productFieldNames = useCallback((productIndex: number) => {
    const fieldNamePrefix =
      productIndex === 0 ? '' : `products[${productIndex}].`;
    return [
      `${fieldNamePrefix}${PoFormField.Supplier}`,
      `${fieldNamePrefix}${PoFormField.Brand}`,
      `${fieldNamePrefix}${PoFormField.Model}`,
      `${fieldNamePrefix}${PoFormField.Condition}`,
      `${fieldNamePrefix}${PoFormField.SupplierOrderNo}`,
      `${fieldNamePrefix}${PoFormField.Description}`,
      `${fieldNamePrefix}${PoFormField.Department}`,
      `${fieldNamePrefix}${PoFormField.SubDepartment}`,
      `${fieldNamePrefix}${PoFormField.Bracket}`,
      `${fieldNamePrefix}${PoFormField.ItemType}`,
      `${fieldNamePrefix}${PoFormField.ItemSubType}`,
      `${fieldNamePrefix}${PoFormField.CostPerItem}`,
      `${fieldNamePrefix}${PoFormField.StartClassificationsSelect}`,
    ];
  }, []);

  const addProduct = useCallback(() => {
    setProductIndexes((currentProductIndexes) => {
      if (currentProductIndexes.length >= MAX_PRODUCTS) {
        return currentProductIndexes;
      }

      const nextIndex = [0, 1, 2, 3].find(
        (index) => !currentProductIndexes.includes(index)
      );

      return nextIndex === undefined
        ? currentProductIndexes
        : [...currentProductIndexes, nextIndex];
    });
  }, []);

  const removeProduct = useCallback(
    (productIndex: number) => {
      if (productIndexes.length === 1) {
        return;
      }

      // Remove the index first so React unmounts the child components,
      // then defer field cleanup to avoid crashing mounted Controllers.
      setProductIndexes((currentProductIndexes) =>
        currentProductIndexes.filter((index) => index !== productIndex)
      );

      // Defer unregister until after the component tree re-renders
      // and the removed product's Controllers have unmounted.
      setTimeout(() => {
        productFieldNames(productIndex).forEach((fieldName) => {
          unregister(fieldName);
        });
        setValue(`allocations[${productIndex}].quantity`, '', {
          shouldDirty: true,
        });
        setValue(`allocations[${productIndex}].state`, '');
        setValue(`allocations[${productIndex}].storeNum`, '');
        clearErrors(`allocations[${productIndex}].quantity`);
        clearErrors(productFieldNames(productIndex));
      }, 0);
    },
    [
      clearErrors,
      productFieldNames,
      productIndexes.length,
      setValue,
      unregister,
    ]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const calculatePricing = useCallback(async () => {
    const formValues = getValues();
    const purchaseOrderSubType = getValues(PoFormField.PurchaseOrderSubType);
    const referenceNumber = getValues(PoFormField.ReferenceNumber);
    let currentPrimaryState = getValues(PRIMARY_ALLOCATION_STATE_FIELD);
    const currentPrimaryStoreNum = getValues(PRIMARY_ALLOCATION_STORE_FIELD);

    // If state is missing but storeNum is available, resolve it
    if (!currentPrimaryState && currentPrimaryStoreNum) {
      try {
        const storeData = await getStoreDetailByNumber(currentPrimaryStoreNum);
        const storeDetail = storeData?.response?.[0];
        currentPrimaryState = storeDetail?.stateAbb || '';
        if (currentPrimaryState) {
          setValue(PRIMARY_ALLOCATION_STATE_FIELD, currentPrimaryState, {
            shouldDirty: true,
          });
        }
      } catch {
        // state remains empty, validation will catch it
      }
    }

    const getProductFieldName = (
      productIndex: number,
      fieldName: PoFormField
    ) =>
      productIndex === 0 ? fieldName : `products[${productIndex}].${fieldName}`;
    const getProductValue = (productIndex: number, fieldName: PoFormField) =>
      productIndex === 0
        ? formValues[fieldName]
        : formValues.products?.[productIndex]?.[fieldName];
    const normalizedProducts = [...(formValues.products || [])];
    const getQuantityFieldName = (productIndex: number) =>
      `allocations[${productIndex}].quantity`;
    const getAllocationQuantity = (productIndex: number) =>
      getValues(getQuantityFieldName(productIndex)) ||
      getInputValue(getQuantityFieldName(productIndex));

    productIndexes.forEach((productIndex) => {
      normalizedProducts[productIndex] = LOCAL_SOURCE_PRODUCT_FIELDS.reduce(
        (product: any, fieldName: PoFormField) => ({
          ...product,
          [fieldName]: getProductValue(productIndex, fieldName),
        }),
        {}
      );
    });
    setValue('products', normalizedProducts, {
      shouldDirty: true,
      shouldValidate: false,
    });

    if (currentPrimaryState && currentPrimaryStoreNum) {
      productIndexes.forEach((index) => {
        if (index > 0) {
          setValue(`allocations[${index}].state`, currentPrimaryState, {
            shouldDirty: true,
            shouldValidate: true,
          });
          setValue(`allocations[${index}].storeNum`, currentPrimaryStoreNum, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    }

    productIndexes.forEach((productIndex) => {
      const quantity = getAllocationQuantity(productIndex);

      if (hasRequiredValue(quantity)) {
        setValue(getQuantityFieldName(productIndex), quantity, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });

    const currentAllocations = getValues('allocations');
    const requiredFieldNames = productIndexes.reduce(
      (fieldNames: string[], productIndex: number) => [
        ...fieldNames,
        ...REQUIRED_LOCAL_SOURCE_PRODUCT_FIELDS.map((fieldName) =>
          getProductFieldName(productIndex, fieldName)
        ),
        getQuantityFieldName(productIndex),
      ],
      REQUIRED_LOCAL_SOURCE_FORM_FIELDS
    );
    const missingRequiredFields = [
      {
        fieldName: PoFormField.PurchaseOrderSubType,
        value: purchaseOrderSubType,
      },
      {
        fieldName: PoFormField.ReferenceNumber,
        value: referenceNumber,
      },
      {
        fieldName: PRIMARY_ALLOCATION_STORE_FIELD,
        value: currentPrimaryStoreNum,
      },
      {
        fieldName: PRIMARY_ALLOCATION_STATE_FIELD,
        value: currentPrimaryState,
      },
      ...productIndexes.flatMap((productIndex) => [
        ...REQUIRED_LOCAL_SOURCE_PRODUCT_FIELDS.map((fieldName) => ({
          fieldName: getProductFieldName(productIndex, fieldName),
          value: getProductValue(productIndex, fieldName),
        })),
        {
          fieldName: getQuantityFieldName(productIndex),
          value:
            currentAllocations?.[productIndex]?.quantity ||
            getAllocationQuantity(productIndex),
        },
      ]),
    ].filter(({ value }) => !hasRequiredValue(value));

    if (missingRequiredFields.length) {
      await trigger(requiredFieldNames);
      // eslint-disable-next-line no-console
      console.warn(
        'Calculate Pricing skipped: missing required fields',
        missingRequiredFields
      );
      return;
    }

    clearErrors(requiredFieldNames);
    setValue('localSourceProductIndexes', productIndexes);
    if (!currentPrimaryState) {
      // eslint-disable-next-line no-console
      console.warn('Calculate Pricing skipped: selected store has no state', {
        storeNum: currentPrimaryStoreNum,
        allocations: formValues.allocations,
      });
      return;
    }

    if (currentPrimaryState && currentPrimaryStoreNum) {
      productIndexes.forEach((index) => {
        if (index > 0) {
          setValue(`allocations[${index}].state`, currentPrimaryState);
          setValue(`allocations[${index}].storeNum`, currentPrimaryStoreNum);
        }
      });
    }
    // eslint-disable-next-line no-console
    console.log('Calculate Pricing started', {
      productIndexes,
      storeNum: currentPrimaryStoreNum,
      state: currentPrimaryState,
      allocations: currentAllocations,
      products: normalizedProducts,
    });

    setPricingRequestTrigger((currentTrigger) => currentTrigger + 1);
  }, [clearErrors, getValues, productIndexes, setValue, trigger]);

  return (
    <div className={classes.root} data-testid="local-source-ramp-details">
      {productIndexes.map((productIndex, position) => {
        const existingDetailPO = buildExistingDetailPO(
          purchaseOrder,
          productIndex
        );
        const formProducts = getValues('products');
        const duplicateDetailPO =
          !existingDetailPO && productIndex > 0
            ? buildDuplicateDetailPO(formProducts, productIndex)
            : null;
        const prefillPO =
          prefillPurchaseOrders && prefillPurchaseOrders[productIndex];
        const productPO =
          existingDetailPO ||
          duplicateDetailPO ||
          prefillPO ||
          purchaseOrder ||
          null;
        return (
          <div className={classes.productDetails} key={productIndex}>
            <LocalSourceItemDetails
              purchaseOrder={productPO}
              isViewMode={disabled}
              index={productIndex}
              showHeader={position === 0}
              canAddProduct={!disabled && productIndexes.length < MAX_PRODUCTS}
              onAddProduct={addProduct}
            />
            <LocalSourceInventoryDetails
              purchaseOrder={productPO}
              qrScannerOutput={qrScannerOutput}
              disabled={disabled}
              index={productIndex}
              canRemove={productIndexes.length > 1}
              onRemove={() => removeProduct(productIndex)}
              setIsCoupaOrPcrdSelected={isCoupaOrPcrdSelected}
            />
          </div>
        );
      })}
      <div className={classes.details}>
        <LocalSourceAllocations
          purchaseOrder={purchaseOrder}
          stores={stores}
          onSearch={onSearch}
          isSearchLoading={isSearchLoading}
          disabled={disabled}
          productIndexes={productIndexes}
          onCalculatePricing={calculatePricing}
        />
        <PricingWrapper
          purchaseOrder={purchaseOrder}
          qrScannerOutput={qrScannerOutput}
          disableAutoPricing
          pricingRequestTrigger={pricingRequestTrigger}
          localSourceProductIndexes={productIndexes}
        />
      </div>
    </div>
  );
};
